import type { ColorwayOption } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import type { PdpFormatParam } from './ProductDetailsPage.types';
import { CardFormat } from './constants';

/**
 * Bakes the selected colorway into the template so the created draft opens on
 * the chosen variant. Returns the template unchanged when there's no matching
 * colorway.
 */
export const withSelectedColorway = <T extends MinimalRequiredTemplateDataForDraft>(
  template: T,
  options: ReadonlyArray<ColorwayOption> | undefined,
  selectedColorwayId: string,
): T => {
  const hasColorway = !!(options && selectedColorwayId && options.some(o => o.id === selectedColorwayId));
  if (!hasColorway) {
    return template;
  }
  return {
    ...template,
    themeJSON: {
      ...template.themeJSON,
      card: {
        ...template.themeJSON.card,
        front: {
          ...template.themeJSON.card.front,
          colorways: { ...template.themeJSON.card.front.colorways!, selectedId: selectedColorwayId },
        },
      },
    },
  };
};

const productTypes: Record<string, CardFormat> = {
  stationeryCard: CardFormat.paper,
  digitalCard: CardFormat.premiumDigital,
  digital: CardFormat.digital,
  basicDigitalV2: CardFormat.basicDigitalV2,
};

export const convertProductTypeToCardFormat = (productType: string): CardFormat => {
  return productTypes[productType] ?? CardFormat.paper;
};

// The `?format=` param a PDP navigation should carry so the selected product
// survives a template (size) change. Product types without a CardFormat render
// through the paper PDP and travel as their raw productType, which the PDP's
// product seeding matches before falling back to the CardFormat mapping.
export const convertProductTypeToFormatParam = (productType: string): PdpFormatParam => {
  const mapped = productTypes[productType];
  if (mapped) return mapped;
  if (productType === 'magnet' || productType === 'napkin' || productType === 'sign') return productType;
  return CardFormat.paper;
};

export const convertCardFormatToProductType = (cardFormat: CardFormat): string => {
  return Object.keys(productTypes).find(key => productTypes[key] === cardFormat) ?? 'stationeryCard';
};
